import {
    Box,
    Button,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Dimension,
    Issue,
    IssuePriority,
    IssueStatus,
    QuickQuoteStatus
} from "../../API";
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useMemo,
    useState
} from "react";
import {
    FieldDisplay,
    FieldDisplayData
} from "./FieldDisplay";
import {
    ResourceCard,
    ResourceCardTextField,
    ResourceCardTitle
} from "./ResourceCard";

import { AddIssueImageDialog } from "./AddIssueImageDialog";
import { ExpandableCarousel } from "./image/ExpandableCarousel";
import { ImageEntity } from "../../lib/dashboard/types/ImageEntity";
import { IssuePriorityDisplay } from "./IssuePriorityDisplay";
import { IssueStatusDisplay } from "./IssueStatusDisplay";
import { ItemLabel } from "./ItemLabel";
import { MeasurementUnit } from "../../models";
import { ProcureDimensionsContent } from "./dimensions/ProcureDimensionsContent";
import { QuickQuoteModal } from "./QuickQuoteModal";
import SellIcon from '@mui/icons-material/Sell';
import { TagsControl } from "./TagsControl";
import { TemporaryImage } from "../../componentsV2/image/TemporaryImage";
import { UploadStagingImageCarousel } from "../../componentsV2/image/UploadStagingImageCarousel";
import { ViewEstimatesModal } from "./ViewEstimatesModal";
import WarningIcon from '@mui/icons-material/Warning';
import { format } from "date-fns";
import { formatDimension } from "../../lib/dashboard/FormatDimension";
import { issuePriorityText } from "../../componentsV2/issue/issuesPriorityText";
import { issueStatusText } from "../../componentsV2/issue/issuesStatusText";
import { toFieldDisplayData } from "../../lib/dashboard/StringUtils";
import { useAssessments } from "./contexts/AssessmentsContextProvider";
import { useIssues } from "./contexts/IssuesContextProvider";
import { useResolveImageEntity } from "../../lib/dashboard/hooks/useResolveImageEntity";
import { useSubmitDimensions } from "./data/hooks/useSubmitDimensions";
import { useTags } from "./contexts/TagsContextProvider";

type IssueCardProps = {
    issue: Issue;
    images: Array<ImageEntity>;
    inFocus: boolean;
    onEditIssueSubmit?: (id: string, issue: Issue) => Promise<void>;
    hideQuickQuote?: boolean;
    showDimensionsForm?: boolean;
    dimensionFormRef?: React.RefObject<HTMLFormElement>;
    showUploadStagingImages?: boolean;
    stagedImages?: TemporaryImage[];
    setStagedImages?: Dispatch<SetStateAction<TemporaryImage[]>>;
};

const LABEL_COLOR = "error";
const LABEL_ICON = WarningIcon;
const BG_COLOR = {
    r: 193,
    g: 41,
    b: 46
};

export const IssueCard = (props: IssueCardProps) => {
    const {
        issue,
        images,
        inFocus,
        onEditIssueSubmit,
        hideQuickQuote,
        showDimensionsForm,
        dimensionFormRef,
        showUploadStagingImages,
        stagedImages,
        setStagedImages
    } = props;

    const [showQuickQuoteModal, setShowQuickQuoteModal] = useState(false);
    const [showEstimateModal, setShowEstimateModal] = useState(false);
    const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

    const { resolvedImageEntities } = useResolveImageEntity(images);
    const { tags, createTag } = useTags();
    const { assessments } = useAssessments();

    const withAssessment = useMemo<boolean>(() => {
        return assessments.some(assessment => assessment.issueIds?.includes(issue.id));
    }, [assessments, issue.id]);

    const {
        parentIdToDimensionMap,
        submitQuickQuotes,
        isQuickQuoteSubmitting,
        issueIdToQuickQuoteMap,
        issueIdToProposalItemPriceTotalMap
    } = useIssues();

    const { submitDimensions } = useSubmitDimensions();

    const handleOpenQuickQuoteModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setShowQuickQuoteModal(true);
    };

    const handleCloseQuickQuoteModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setShowQuickQuoteModal(false);
    };

    const quickQuoteStarted = useMemo<boolean>(() => {
        return (issue?.quickQuoteIds?.length ?? 0) > 0;
    }, [issue]);

    const showQuickQuote = !hideQuickQuote && submitQuickQuotes;

    const quickQuoteHelperMessage = useMemo<string>(() => {
        if (!showQuickQuote) {
            return "";
        }
        if (quickQuoteStarted || isQuickQuoteSubmitting) {
            return "Quick Estimate in progress";
        }
        return "";
    }, [images, props, submitQuickQuotes]);

    const quickQuote = issueIdToQuickQuoteMap.get((props as IssueCardProps).issue?.id);
    const viewEstimateHelperMessage = quickQuote?.status !== QuickQuoteStatus.REVIEWED ?
        "Estimate in progress" :
        "";

    const submittedAtString = useMemo<string>(() => {
        if (issue.clientCreationDate) {
            return format(new Date(issue.clientCreationDate), "yyyy-MM-dd h:mm a");
        }
        return format(new Date(issue.createdAt!), "yyyy-MM-dd h:mm a");
    }, [issue]);

    const noteFieldDisplayProps: Array<FieldDisplayData<string>> = toFieldDisplayData(issue.note || "-", "Note");
    const dimension: Dimension | undefined = parentIdToDimensionMap.get(issue.id);
    const onNoteFieldEdit = useCallback((label: string, data: string) => {
        const newNote = [
            ...noteFieldDisplayProps.filter((props) => props.label !== label),
            { label, data }
        ]
            // sort by original order
            .sort((a, b) => noteFieldDisplayProps.findIndex((props) => props.label === a.label) - noteFieldDisplayProps.findIndex((props) => props.label === b.label))
            .filter((props) => {
                const alwaysPreserve = ["description", "submitted by"];
                if (props.label && alwaysPreserve.includes(props.label.toLowerCase())) {
                    return true;
                }
                return !!data;
            })
            .map((props) => {
                if (props.label === "Note") {
                    return data;
                }
                return `${props.label}: ${props.data}`;
            })
            .join("\n");
        onEditIssueSubmit?.(issue.id, { ...issue, note: newNote });
    }, [issue, noteFieldDisplayProps, onEditIssueSubmit]);
    
    const proposalItemPriceTotal = issueIdToProposalItemPriceTotalMap?.get(issue.id);

    return (
        <ResourceCard
            inFocus={inFocus}
            imageCarousel={(
                <>
                    {showUploadStagingImages && stagedImages && setStagedImages ? (
                        <UploadStagingImageCarousel
                            imageDTOs={stagedImages}
                            setImageDTOs={setStagedImages}
                        />
                    ) : (
                        <ExpandableCarousel
                            images={resolvedImageEntities}
                            showUploadImages={!!onEditIssueSubmit}
                            onUploadImagesClick={() => setIsImageDialogOpen(true)}
                        />
                    )}
                    {showUploadStagingImages && stagedImages && stagedImages.length === 0 && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <WarningIcon color="warning" fontSize="small" />
                            Consider adding images to improve estimate accuracy
                        </Typography>
                    )}
                </>
            )}
            borderColor="error.main"
            bgColor={BG_COLOR}
            content={
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    flex={3}
                    justifyContent="space-around"
                >
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" justifyContent="space-between" alignItems="center" position="relative" gap={1}>
                            <FieldDisplay
                                data={issue.name || ""}
                                onEditSubmit={onEditIssueSubmit ? (data: string) => {
                                    onEditIssueSubmit?.(issue.id, { ...issue, name: data });
                                } : undefined}
                                readView={<ResourceCardTitle>{issue.name}</ResourceCardTitle>}
                                editView={({ data, setData }) => (
                                    <ResourceCardTextField
                                        value={data}
                                        onChange={(e) => setData(e.target.value)}
                                        sx={{ flex: 1 }}
                                        InputProps={{
                                            sx: { fontWeight: 700, fontSize: "1.25rem" }
                                        }}
                                        size="small"
                                    />
                                )}
                            />
                            <Box display="flex" gap={1}>
                                {showQuickQuote && !quickQuote && (
                                    <Tooltip title={quickQuoteHelperMessage} arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                onClick={handleOpenQuickQuoteModal}
                                                disabled={!!quickQuoteHelperMessage}
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                Quick Estimate
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {showQuickQuote && quickQuote && (
                                    <Tooltip title={viewEstimateHelperMessage} arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setShowEstimateModal(true)}
                                                disabled={quickQuote?.status !== QuickQuoteStatus.REVIEWED}
                                            >
                                                View Estimate
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {proposalItemPriceTotal && (
                                    <Box
                                        display="flex"
                                        gap={0.5}
                                        color={theme => theme.palette.success.main}
                                        bgcolor={theme => theme.palette.success.light}
                                        px={0.5}
                                        py={0.25}
                                    >
                                        <SellIcon color="inherit" />
                                        <Typography color="inherit" fontWeight={700}>
                                            {`$${proposalItemPriceTotal}`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <ItemLabel
                            text={withAssessment ? "Attention Needed" : "Unscheduled Issue"}
                            color={LABEL_COLOR}
                            Icon={LABEL_ICON}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column">
                        <FieldDisplay<any>
                            data={issue.status ?? IssueStatus.SUBMITTED}
                            label="Status"
                            readView={<IssueStatusDisplay status={issue.status ?? IssueStatus.SUBMITTED} />}
                            onEditSubmit={onEditIssueSubmit ? (data) => {
                                onEditIssueSubmit?.(issue.id, { ...issue, status: data });
                            } : undefined}
                            editView={({ data, setData }) => (
                                <ResourceCardTextField
                                    select
                                    SelectProps={{ native: true }}
                                    id="status"
                                    name="status"
                                    value={data}
                                    onChange={(e) => setData(e.target.value)}
                                >
                                    {Object.values(IssueStatus).map((status) => (
                                        <option key={status} value={status}>{issueStatusText[status]}</option>
                                    ))}
                                </ResourceCardTextField>
                            )}
                        />
                        {noteFieldDisplayProps.map((fieldDisplayProps) => (
                            <FieldDisplay
                                key={fieldDisplayProps.label}
                                label={fieldDisplayProps.label}
                                data={fieldDisplayProps.data || "-"}
                                onEditSubmit={onEditIssueSubmit && fieldDisplayProps.label?.toLowerCase() !== "submitted by" ?
                                    (data) => onNoteFieldEdit(fieldDisplayProps.label!, data) : undefined}
                                editView={({ data, setData }) => (
                                    <ResourceCardTextField
                                        value={data}
                                        onChange={(e) => setData(e.target.value)}
                                    />
                                )}
                            />
                        ))}
                        <FieldDisplay<any>
                            data={issue.priority ?? IssuePriority.MEDIUM}
                            label="Priority"
                            readView={<IssuePriorityDisplay priority={issue.priority ?? IssuePriority.MEDIUM} />}
                            onEditSubmit={onEditIssueSubmit ? (data) => {
                                onEditIssueSubmit?.(issue.id, { ...issue, priority: data });
                            } : undefined}
                            editView={({ data, setData }) => (
                                <ResourceCardTextField
                                    select
                                    SelectProps={{ native: true }}
                                    id="priority"
                                    name="priority"
                                    value={data}
                                    onChange={(e) => setData(e.target.value)}
                                >
                                    {Object.values(IssuePriority).map((priority) => (
                                        <option key={priority} value={priority}>{issuePriorityText[priority]}</option>
                                    ))}
                                </ResourceCardTextField>
                            )}

                        />
                        <FieldDisplay
                            label="Dimensions"
                            readView={dimension ? formatDimension(dimension) : "-"}
                            data={dimension ?? {
                                measurement: {
                                    length: 0,
                                    width: 0,
                                    measurementUnit: MeasurementUnit.FOOT
                                }
                            } as Dimension}
                            onEditSubmit={submitDimensions ? async (data: Dimension) => {
                                const formData = new FormData();
                                formData.append("length", data.measurement?.length?.toString() ?? "0");
                                formData.append("width", data.measurement?.width?.toString() ?? "0");
                                formData.append("unit", data.measurement?.measurementUnit ?? MeasurementUnit.FOOT);
                                await submitDimensions(issue.id, formData);
                            } : undefined}
                            editView={({ data, setData }) => (
                                <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
                                    <ResourceCardTextField
                                        type="number"
                                        id="length"
                                        name="length"
                                        label="Length"
                                        variant="outlined"
                                        size="small"
                                        sx={{ maxWidth: "100px", backgroundColor: "white" }}
                                        value={data.measurement?.length ?? ""}
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => setData(prev => {
                                            const newLength = parseFloat(e.target.value);
                                            return {
                                                ...prev, measurement: {
                                                    __typename: "Measurement",
                                                    ...(prev.measurement ?? {}),
                                                    length: isNaN(newLength) ? null : newLength
                                                }
                                            };
                                        })}
                                    />
                                    <ResourceCardTextField
                                        type="number"
                                        id="width"
                                        name="width"
                                        label="Width"
                                        variant="outlined"
                                        size="small"
                                        sx={{ maxWidth: "100px", backgroundColor: "white" }}
                                        inputProps={{ min: 0 }}
                                        value={data.measurement?.width ?? ""}
                                        onChange={(e) => setData(prev => {
                                            const newWidth = parseFloat(e.target.value);
                                            return {
                                                ...prev, measurement: {
                                                    __typename: "Measurement",
                                                    ...(prev.measurement ?? {}),
                                                    width: isNaN(newWidth) ? null : newWidth
                                                }
                                            };
                                        })}
                                    />
                                    <ResourceCardTextField
                                        select
                                        SelectProps={{ native: true }}
                                        id="unit"
                                        name="unit"
                                        value={data.measurement?.measurementUnit ?? MeasurementUnit.FOOT}
                                        label="Unit"
                                        onChange={(e) => setData({
                                            ...data, measurement: {
                                                __typename: "Measurement",
                                                ...(data.measurement ?? {}),
                                                measurementUnit: e.target.value as MeasurementUnit
                                            }
                                        })}
                                        sx={{ maxWidth: "100px", backgroundColor: "white" }}
                                    >
                                        {Object.values(MeasurementUnit).map((unitValue) => (
                                            <option key={unitValue} value={unitValue}>
                                                {unitValue}
                                            </option>
                                        ))}
                                    </ResourceCardTextField>
                                </Box>
                            )}
                        />
                        <FieldDisplay label="Reported" data={submittedAtString} />
                        <TagsControl
                            options={tags}
                            selectedTagIds={issue.tagIds || []}
                            onTagChange={onEditIssueSubmit ? (tagIds: Array<string>) => {
                                onEditIssueSubmit?.(issue.id, { ...issue, tagIds });
                            } : undefined}
                            createTag={createTag}
                        />
                    </Box>
                    {showQuickQuote && (
                        <QuickQuoteModal
                            open={showQuickQuoteModal}
                            onClose={handleCloseQuickQuoteModal}
                            issueIds={[(props as IssueCardProps).issue.id]}
                        />
                    )}
                    {showEstimateModal && (
                        <ViewEstimatesModal
                            open={showEstimateModal}
                            onClose={() => setShowEstimateModal(false)}
                            issueId={(props as IssueCardProps).issue.id}
                        />
                    )}
                    {showDimensionsForm && (
                        <ProcureDimensionsContent
                            issueId={(props as IssueCardProps).issue.id}
                            ref={dimensionFormRef}
                        />
                    )}
                    <AddIssueImageDialog
                        issue={issue}
                        open={isImageDialogOpen}
                        onClose={() => setIsImageDialogOpen(false)}
                    />
                </Box>
            }
        />
    );
};
